import { BIOBANK_STATUS } from '../definitions/BioBankConstants';
import html2pdf from 'html2pdf.js';

import biopankkiFooterLogo from '../images/biobankki-footer-logos.png';

import biopankkiHeaderLogo from '../images/biobankki-logo.png';

import biopankkiFooterLogoNew from '../images/biopankki-footer-logos-new.png';
/**
 * Checks if consent data status is set either to CONSENT or REFUSAL.
 *
 * @param data The consent data to check
 * @returns {boolean} Returns true if status is either CONSENT or REFUSAL
 */
export const hasActiveStatus = (data) =>
  data.status === BIOBANK_STATUS.CONSENT ||
  data.status === BIOBANK_STATUS.REFUSAL;

/**
 * Checks if consent data status is CONSENT
 *
 * @param data The consent data to check
 * @returns {boolean} Returns true if status is CONSENT
 */
export const hasConsent = (data) => data.status === BIOBANK_STATUS.CONSENT;

/**
 * Biobank refusal version that is used in splitting API content into paragraphs
 * This is the only valid version for required paragraph splitting.
 * In all other cases disregard splitting and default back to full content with no splitting.
 * Do not change this unless you know what you're doing.
 * @type {string}
 */
export const biobankRefusalVersion = "8.0_K";
export const biobankConsentVersion = "8.0_S";

function isNewVersion(ver) {
  return ver === biobankRefusalVersion || ver === biobankConsentVersion;
}

/**
 * Callback for creating footer and header for pdf.
 *
 * @param options dynamic texts to be included above the footer
 * @returns {Function} pdf callback given to html2pdf.js
 */
export const pdfCallback = (options, intl) => (pdf) => {
  let { version, type, date } = options;

  let documentDescription;

  if (isNewVersion(version)) {
    documentDescription = intl.formatMessage(
        { id: `biobank.print.footer.${type}.version.new` },
        { version }
    );
  } else {
    documentDescription = intl.formatMessage(
        { id: `biobank.print.footer.${type}.version.old` },
        { version }
    );
  }

  for (let i = 1; i <= pdf.internal.getNumberOfPages(); i++) {
    pdf.setPage(i);
    pdf.setFontSize(10);

    // a4 = 210 mm x 297 mm
    // margins 10 mm

    let headerConfig = {
      leftColStart: 5,
      middleColStart: 67,
      rightColStart: 144,
      firstRowStart: 10,
      secondRowStart: 15,
      thirdRowStart: 20,
    };

    // header left column image
    let pdfHeaderImg = document.createElement('img');
    pdfHeaderImg.src = biopankkiHeaderLogo;
    pdf.addImage(pdfHeaderImg, 'JPEG', headerConfig.leftColStart, 5, 54, 18);

    // header middle column
    pdf.text(
      'Tampereen Biopankki',
      headerConfig.middleColStart,
      headerConfig.firstRowStart
    );
    pdf.text(
      'Tays, FM 1-rakennus, 1. kerros',
      headerConfig.middleColStart,
      headerConfig.secondRowStart
    );
    pdf.text(
      'Arvo Ylpön katu 6, 33520 Tampere',
      headerConfig.middleColStart,
      headerConfig.thirdRowStart
    );

    // line rgb(7,49,132)
    pdf.setDrawColor(7, 49, 132);
    pdf.setLineWidth(0.3);
    pdf.line(11, 25, 199, 25);

    // footer line
    pdf.line(11, 272.5, 199, 272.5);

    // type and version
    pdf.text(documentDescription, 11, 270);
    pdf.text(date, 181, 270);

    let pdfFooterImg = document.createElement('img');

    if (isNewVersion(version)) {
      // header right column
      pdf.text(
          '+358 3 311 65205',
          headerConfig.rightColStart,
          headerConfig.firstRowStart
      );
      pdf.text(
          'biopankki@pirha.fi',
          headerConfig.rightColStart,
          headerConfig.secondRowStart
      );
      pdf.text(
          'www.pirha.fi/tampereen-biopankki',
          headerConfig.rightColStart,
          headerConfig.thirdRowStart
      );
      pdfFooterImg.src = biopankkiFooterLogoNew;
      pdf.addImage(pdfFooterImg, 'JPEG', 11, 275, 188, 13);
    } else {
      // header right column
      pdf.text(
          '+358 3 311 6520',
          headerConfig.rightColStart,
          headerConfig.firstRowStart
      );
      pdf.text(
          'biopankki@tays.fi',
          headerConfig.rightColStart,
          headerConfig.secondRowStart
      );
      pdf.text(
          'www.tays.fi/biopankki',
          headerConfig.rightColStart,
          headerConfig.thirdRowStart
      );
      pdfFooterImg.src = biopankkiFooterLogo;
      pdf.addImage(pdfFooterImg, 'JPEG', 11, 273, 188, 13);
    }
  }
};

/**
 * Print given element as pdf
 * @param filename The resulting name of the file
 * @param elementId The html element to print
 */
export const printPdf = (filename, elementId, pdfCallback) => {
  let element = document.getElementById(elementId);
  window.scrollTo(0, 0);
  html2pdf()
    .set({
      filename: filename,
      pagebreak: { after: '.page-break' },
      image: { type: 'jpeg', quality: 1 },
      margin: [32, 10],
      jsPDF: { unit: 'mm', format: 'a4' },
      html2canvas: {
        letterRendering: true,
        scale: 1,
      },
    })
    .from(element)
    .toPdf()
    .get('pdf')
    .then(pdfCallback)
    .save();
};

export const getRefusalPdfParagraphs = (contentText) => {
  let refusalParagraphs = contentText.split("<p>");
  refusalParagraphs = refusalParagraphs.map(p => {
    if(p.includes("</p>")) {
      return p.substring(0, p.indexOf("<"));
    } else {
      return p;
    }
  })
  return refusalParagraphs;
}
